// frontend/src/utils/pdfGenerator.js
import moment from 'moment-timezone';
import { cashierContext } from '../context/CashierContext.js';
import { Notification } from '../components/Notification.js';

const VENEZUELA_TIMEZONE = 'America/Caracas';

export const loadPdfLibraries = async () => {
    if (!window.jspdf) {
        await new Promise(r => {
            const s = document.createElement('script');
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
            s.onload = r;
            document.head.appendChild(s);
        });
    }
    if (!window.jspdf.plugin?.autotable) {
        await new Promise(r => {
            const s = document.createElement('script');
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.28/jspdf.plugin.autotable.min.js";
            s.onload = r;
            document.head.appendChild(s);
        });
    }
};

export const generateDailySchedulePDF = async (allRaces, allTracks) => {
    await loadPdfLibraries();
    Notification(document.body, 'Generando Cartelera...', 'info');
    
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const DARK_GREEN = [15, 56, 26]; 
    const GOLD = [212, 175, 55]; 
    const todayStr = moment().tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY');

    // --- ENCABEZADO ---
    doc.setFillColor(...DARK_GREEN); 
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(...GOLD); 
    doc.setFontSize(22); 
    doc.setFont('helvetica', 'bold');
    doc.text("CARTELERA HÍPICA OFICIAL", 105, 18, { align: 'center' });
    doc.setTextColor(255, 255, 255); 
    doc.setFontSize(12);
    doc.text(`Fecha: ${todayStr}`, 105, 26, { align: 'center' });

    // --- PROCESAMIENTO DE DATOS ---
    const tracksMap = new Map();
    allTracks.forEach(t => tracksMap.set(t.id, t.name));

    const activeTracksMap = {};
    allRaces.forEach(race => {
        if (!activeTracksMap[race.track_id]) {
            activeTracksMap[race.track_id] = { 
                name: race.track_name || tracksMap.get(race.track_id) || 'Hipódromo', 
                races: [] 
            };
        }
        activeTracksMap[race.track_id].races.push(race);
    });

    let finalY = 40;

    if (Object.keys(activeTracksMap).length === 0) {
        doc.setTextColor(0,0,0); doc.setFontSize(16);
        doc.text("NO HAY CARRERAS PROGRAMADAS", 105, 50, { align: 'center' });
    } else {
        // Generamos una tabla separada por cada Hipódromo usando autoTable (evita letras montadas)
        Object.values(activeTracksMap).forEach(trackData => {
            trackData.races.sort((a, b) => a.race_number - b.race_number);
            
            // Título del Hipódromo
            doc.setFontSize(14); 
            doc.setTextColor(...DARK_GREEN);
            doc.setFont('helvetica', 'bold');
            
            // Si no hay espacio para el título y unas filas, forzamos página nueva
            if (finalY > 260) { doc.addPage(); finalY = 20; }
            doc.text(`📍 ${trackData.name.toUpperCase()} (${trackData.races.length} Carreras)`, 15, finalY);
            
            const bodyData = trackData.races.map(r => {
                const time = moment.utc(r.start_time).tz(VENEZUELA_TIMEZONE).format('hh:mm A');
                const runnersCount = (r.runners || []).filter(h => !h.is_scratched).length;
                const statusMap = { 'open': 'ABIERTA', 'scheduled': 'PROGRAMADA', 'closed': 'CERRADA', 'finished': 'FINALIZADA' };
                return [
                    `#${r.race_number}`, 
                    r.name || 'Carrera Regular', 
                    time, 
                    runnersCount,
                    statusMap[r.status] || r.status
                ];
            });

            doc.autoTable({
                startY: finalY + 5,
                head: [['CARRERA', 'NOMBRE', 'HORA', 'EJEMPLARES', 'ESTADO']],
                body: bodyData,
                theme: 'grid',
                headStyles: { fillColor: DARK_GREEN, textColor: GOLD, halign: 'center' },
                columnStyles: { 
                    0: { halign: 'center', fontStyle: 'bold' }, 
                    2: { halign: 'center' }, 
                    3: { halign: 'center' }, 
                    4: { halign: 'center', fontStyle: 'bold' } 
                },
                margin: { left: 15, right: 15 },
                alternateRowStyles: { fillColor: [245, 255, 245] }
            });
            
            finalY = doc.lastAutoTable.finalY + 15;
        });
    }

    // --- COMPARTIR O DESCARGAR ---
    const fileName = `Cartelera_Hipica_${moment().format('DDMM')}.pdf`;
    const pdfBlob = doc.output('blob'); 
    const file = new File([pdfBlob], fileName, { type: "application/pdf" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ title: 'Cartelera Hípica', files: [file] }); } catch (e) { console.log(e); }
    } else { doc.save(fileName); }
};

export const generateGazettePDF = async (track, trackRaces, action = 'share') => {
    if (!track || !trackRaces || trackRaces.length === 0) return;
    Notification(document.body, 'Generando Gaceta...', 'info');

    await loadPdfLibraries();
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const GOLD = [212, 175, 55];
    const DARK_GREEN = [15, 56, 26];

    // Función auxiliar para dibujar el encabezado en cada página
    const drawHeader = () => {
        doc.setFillColor(...DARK_GREEN);
        doc.rect(0, 0, 210, 30, 'F');
        doc.setTextColor(...GOLD); 
        doc.setFontSize(22); 
        doc.setFont('helvetica', 'bold');
        doc.text("GACETA HÍPICA OFICIAL", 105, 18, { align: 'center' });
        
        doc.setFontSize(12); 
        doc.setTextColor(255, 255, 255);
        const todayStr = moment().tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY');
        doc.text(`${track.name} | Fecha: ${todayStr}`, 105, 26, { align: 'center' });
    };

    // Ordenamos las carreras
    const sortedRaces = trackRaces.sort((a, b) => a.race_number - b.race_number);

    sortedRaces.forEach((race, index) => {
        // ⭐ FIX: Cada carrera nueva en una hoja nueva (excepto la primera)
        if (index > 0) {
            doc.addPage();
        }
        
        drawHeader(); // Dibujamos el encabezado en la nueva hoja
        let finalY = 40;

        const time = moment.utc(race.start_time).tz(VENEZUELA_TIMEZONE).format('hh:mm A');
        
        // Título de la carrera
        doc.setFontSize(14); 
        doc.setTextColor(...DARK_GREEN); 
        doc.setFont('helvetica', 'bold');
        doc.text(`🏁 Carrera #${race.race_number} - Hora: ${time}`, 15, finalY);

        // ⭐ FIX: Columnas completas incluyendo Odds/Morning Line
        const columns = ["N°", "Ejemplar", "Jinete", "Entrenador", "Odds / ML"];
        const rows = (race.runners || []).sort((a, b) => a.horse_number - b.horse_number).map(h => {
            if (h.is_scratched) return [h.horse_number, `${h.name} (RETIRADO)`, '-', '-', '-'];
            return [
                h.horse_number, 
                h.name, 
                h.jockey || 'N/A', 
                h.trainer || 'N/A', 
                h.morning_line || h.odds || '-' // Aseguramos que muestre el odd
            ];
        });

        doc.autoTable({
            startY: finalY + 5,
            head: [columns],
            body: rows,
            theme: 'grid',
            headStyles: { fillColor: DARK_GREEN, textColor: GOLD, fontStyle: 'bold', halign: 'center' },
            columnStyles: { 
                0: { halign: 'center', fontStyle: 'bold', fillColor: [250, 250, 210], cellWidth: 15 }, 
                1: { fontStyle: 'bold' },
                4: { halign: 'center', fontStyle: 'italic', cellWidth: 25 } // Ancho fijo para Odds
            },
            alternateRowStyles: { fillColor: [245, 255, 245] },
            margin: { left: 15, right: 15, bottom: 20 },
            createdCell: function (data) {
                // Pintar de rojo los caballos retirados
                if (data.row.raw && typeof data.row.raw[1] === 'string' && data.row.raw[1].includes('(RETIRADO)')) {
                    data.cell.styles.textColor = [239, 68, 68]; 
                    data.cell.styles.fontStyle = 'italic';
                }
            }
        });
    });

    // --- PIE DE PÁGINA GLOBAL ---
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(...DARK_GREEN);
        doc.rect(0, 282, 210, 15, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text(`Sistema Taquilla Hípica - Página ${i} de ${pageCount}`, 105, 291, { align: 'center' });
    }

    const fileName = `Gaceta_${track.name.replace(/\s+/g, '_')}_${moment().format('DDMM')}.pdf`;
    if (action === 'download') {
        doc.save(fileName);
        Notification(document.body, 'Gaceta descargada.', 'success');
    } else {
        const pdfBlob = doc.output('blob');
        const file = new File([pdfBlob], fileName, { type: "application/pdf" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            navigator.share({ title: `Gaceta: ${track.name}`, files: [file] }).catch(e => console.log(e));
        } else {
            doc.save(fileName);
        }
    }
};

export const generateResultsPDF = async (races, isSingle = false) => {
    await loadPdfLibraries();
    
    Notification(document.body, 'Generando PDF de Resultados...', 'info');
    
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: isSingle ? 'portrait' : 'landscape', unit: 'mm', format: 'a4' });

    const GOLD = [212, 175, 55];
    const DARK_GREEN = [6, 78, 59];
    const LIGHT_GREEN = [240, 253, 244];

    doc.setFillColor(...DARK_GREEN);
    doc.rect(0, 0, 297, 30, 'F'); 
    
    doc.setTextColor(...GOLD);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    const titleText = isSingle ? "RESULTADO OFICIAL DE CARRERA" : "REPORTE DE RESULTADOS";
    doc.text(titleText, doc.internal.pageSize.width / 2, 18, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text(`Generado: ${moment().format('DD/MM/YYYY HH:mm A')}`, doc.internal.pageSize.width / 2, 25, { align: 'center' });

    let startY = 40;

    if (isSingle) {
        const race = races[0];
        const raceTime = moment.utc(race.start_time).tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY - HH:mm A');

        doc.setDrawColor(...GOLD);
        doc.setLineWidth(1);
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(15, 35, 180, 25, 3, 3, 'FD');

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(14);
        doc.text(`Hipódromo: ${race.track_name}`, 20, 45);
        doc.text(`Carrera N°: ${race.race_number}`, 20, 55);
        doc.text(`Fecha: ${raceTime}`, 120, 45);
        doc.text(`Estatus: FINALIZADA`, 120, 55);

        const first = race.results.find(r => r.position === 1);
        const second = race.results.find(r => r.position === 2);
        const third = race.results.find(r => r.position === 3);

        const bodyData = [
            ['1° LUGAR', first ? `#${first.horse_number} ${first.horse_name}` : 'N/A', first ? `$${race.win_payoff || '0.00'}` : '-'],
            ['2° LUGAR', second ? `#${second.horse_number} ${second.horse_name}` : 'N/A', second ? `$${race.place_payoff || '0.00'}` : '-'],
            ['3° LUGAR', third ? `#${third.horse_number} ${third.horse_name}` : 'N/A', third ? `$${race.trifecta_payoff || '0.00'}` : '-']
        ];

        doc.autoTable({
            startY: 70,
            head: [['POSICIÓN', 'EJEMPLAR', 'DIVIDENDO (Gan/Place/Show)']],
            body: bodyData,
            theme: 'grid',
            headStyles: { fillColor: DARK_GREEN, textColor: GOLD, fontSize: 12, halign: 'center' },
            bodyStyles: { fontSize: 14, minCellHeight: 15, valign: 'middle' },
            columnStyles: {
                0: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] },
                2: { halign: 'right', fontStyle: 'bold', textColor: [0, 100, 0] }
            }
        });

    } else {
        const columns = ["Hora", "Hipódromo", "Carrera", "1° Ganador", "2° Place", "3° Show"];
        const rows = races.map(r => {
            const time = moment.utc(r.start_time).tz(VENEZUELA_TIMEZONE).format('DD/MM HH:mm');
            const p1 = r.results.find(p => p.position === 1);
            const p2 = r.results.find(p => p.position === 2);
            const p3 = r.results.find(p => p.position === 3);

            return [
                time,
                r.track_name,
                `#${r.race_number}`,
                p1 ? `#${p1.horse_number} ${p1.horse_name}\n($${r.win_payoff})` : '-',
                p2 ? `#${p2.horse_number} ${p2.horse_name}\n($${r.place_payoff})` : '-',
                p3 ? `#${p3.horse_number} ${p3.horse_name}\n($${r.trifecta_payoff})` : '-'
            ];
        });

        doc.autoTable({
            startY: 35,
            head: [columns],
            body: rows,
            theme: 'striped',
            headStyles: { fillColor: DARK_GREEN, textColor: GOLD, halign: 'center' },
            styles: { fontSize: 10, cellPadding: 2, valign: 'middle' },
            alternateRowStyles: { fillColor: LIGHT_GREEN },
        });
    }

    const pdfBlob = doc.output('blob');
    const fileName = isSingle 
        ? `Resultado_${races[0].track_name}_C${races[0].race_number}.pdf` 
        : `Resultados_Hipicos_${moment().format('DDMMYYYY')}.pdf`;
    
    const file = new File([pdfBlob], fileName, { type: "application/pdf" });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
            await navigator.share({
                title: isSingle ? 'Resultado de Carrera' : 'Resultados del Día',
                text: 'Adjunto el reporte oficial de resultados.',
                files: [file]
            });
            Notification(document.body, 'PDF Compartido.', 'success');
        } catch (err) {
            console.warn('Share cancelado o fallido', err);
        }
    } else {
        doc.save(fileName);
        Notification(document.body, 'PDF Descargado.', 'success');
    }
};

export const generateTicketPDFMobile = async (ticketDetails, action) => {
    await loadPdfLibraries();
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [80, 200] }); 
    
    doc.setFont('courier', 'bold');
    doc.setFontSize(10);
    
    let y = 10;
    const centerX = 40;
    
    doc.text("TAQUILLA HÍPICA", centerX, y, { align: 'center' }); y+=5;
    doc.setFontSize(8);
    doc.text(moment().format('DD/MM/YYYY HH:mm'), centerX, y, { align: 'center' }); y+=8;
    
    doc.setFontSize(10);
    doc.text("------------------------", centerX, y, { align: 'center' }); y+=5;
    doc.text(`TICKET: ${ticketDetails.id}`, centerX, y, { align: 'center' }); y+=5;
    doc.text("------------------------", centerX, y, { align: 'center' }); y+=8;
    
    doc.text(`Hipódromo: ${ticketDetails.track_name}`, 5, y); y+=5;
    doc.text(`Carrera: #${ticketDetails.race_number}`, 5, y); y+=5;
    doc.text(`Apuesta: ${ticketDetails.bet_type_name}`, 5, y); y+=5;
    
    const isMixto = ticketDetails.bet_type_name === 'Mixto';
    let horsesString = 'N/A';
    if (ticketDetails.selected_horse_names) {
        horsesString = isMixto 
            ? ticketDetails.selected_horse_names.map(h => `• ${h}`).join('\n') 
            : ticketDetails.selected_horse_names.join(', ');
    }
    
    const horsesText = doc.splitTextToSize(`Ejemplar(es):\n${horsesString}`, 70);
    doc.text(horsesText, 5, y); 
    y += (horsesText.length * 4) + 3; 
    
    doc.setFontSize(12);
    const amountTxt = `${ticketDetails.currency} ${parseFloat(ticketDetails.amount).toFixed(2)}`;
    doc.text(`MONTO: ${amountTxt}`, centerX, y, { align: 'center' }); y+=10;
    
    try {
        if (window.QRCode) {
            const qrCanvas = document.createElement('canvas');
            await window.QRCode.toCanvas(qrCanvas, ticketDetails.ticket_code, { width: 100 });
            const qrData = qrCanvas.toDataURL('image/jpeg');
            doc.addImage(qrData, 'JPEG', 25, y, 30, 30);
            y += 35;
        }
    } catch(e) { y += 10; }
    
    doc.setFontSize(8);
    doc.text("Conserve este boleto", centerX, y, { align: 'center' });

    const fileName = `Ticket_${ticketDetails.ticket_code}.pdf`;

    if (action === 'download') {
        doc.save(fileName);
    } else if (action === 'share') {
        const pdfBlob = doc.output('blob');
        const file = new File([pdfBlob], fileName, { type: "application/pdf" });
        if (navigator.share) {
            navigator.share({ title: `Boleto: ${ticketDetails.ticket_code}`, files: [file] }).catch(e=>{});
        } else {
            doc.save(fileName);
        }
    }
};